import { useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useContact, useUpdateContact, useAddDataSource, useUploadDocument } from '../hooks/useContacts';
import { useStartAnalysis } from '../hooks/useAnalysis';
import { useTrackingDashboard, useTrackingSnippet, usePurgeTracking } from '../hooks/useTracking';
import Badge from '../components/ui/Badge';
import ScorePill from '../components/ui/ScorePill';
import Bar from '../components/ui/Bar';
import Modal from '../components/ui/Modal';
import { useTags } from '../hooks/useTags';
import { useLlmProviders } from '../hooks/useLlm';
import { useReports } from '../hooks/useReports';

const DATA_SOURCE_TYPES = [
  { value: 'google_analytics', label: 'Google Analytics', icon: '📊' },
  { value: 'search_console', label: 'Search Console', icon: '🔍' },
  { value: 'tracking', label: 'Tracking-Snippet', icon: '📡' },
  { value: 'manual', label: 'Manuell (CSV/Export)', icon: '📄' },
];

export default function ContactDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: contact, isLoading } = useContact(id);
  const updateContact = useUpdateContact();
  const addDataSource = useAddDataSource();
  const uploadDocument = useUploadDocument();
  const startAnalysis = useStartAnalysis();
  const { data: allTags = [] } = useTags();
  const { data: llmData } = useLlmProviders();
  const providers = llmData?.providers || [];
  const { data: reportsData } = useReports();
  const customerReports = (reportsData?.data || []).filter(r => r.analysis?.contact_id === parseInt(id));
  const fileRef = useRef(null);

  const [tab, setTab] = useState('overview');
  const [showAnalysis, setShowAnalysis] = useState(false);
  const [analysisForm, setAnalysisForm] = useState({ url: '', page_type: '', provider: '' });
  const [showDataSource, setShowDataSource] = useState(false);
  const [dsForm, setDsForm] = useState({ type: '', label: '' });
  const [trackingDays, setTrackingDays] = useState(30);
  const [showSnippet, setShowSnippet] = useState(false);
  const [showPurgeConfirm, setShowPurgeConfirm] = useState(false);

  const trackingData = useTrackingDashboard(contact?.tracking_enabled ? id : null, trackingDays);
  const snippetData = useTrackingSnippet(showSnippet && contact?.tracking_enabled ? id : null);
  const purgeTracking = usePurgeTracking();

  if (isLoading || !contact) {
    return <div className="flex items-center justify-center h-64 text-gray-400">Wird geladen…</div>;
  }

  const handleStartAnalysis = async () => {
    if (!analysisForm.url || !analysisForm.page_type) return;
    try {
      await startAnalysis.mutateAsync({
        url: analysisForm.url,
        page_type: analysisForm.page_type,
        contact_id: contact.id,
        provider: analysisForm.provider || undefined,
      });
      setShowAnalysis(false);
      setAnalysisForm({ url: '', page_type: '', provider: '' });
      setTab('analyses');
    } catch (err) {
      alert('Fehler: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      await uploadDocument.mutateAsync({ contactId: contact.id, file });
    } catch (err) {
      alert('Upload fehlgeschlagen: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleAddDataSource = async () => {
    if (!dsForm.type) return;
    try {
      await addDataSource.mutateAsync({ contactId: contact.id, ...dsForm });
      setShowDataSource(false);
      setDsForm({ type: '', label: '' });
    } catch (err) {
      alert('Fehler: ' + (err.response?.data?.message || err.message));
    }
  };

  const toggleTracking = () => {
    updateContact.mutate({ id: contact.id, tracking_enabled: !contact.tracking_enabled });
  };

  const TABS = [
    { key: 'overview', label: 'Übersicht' },
    { key: 'analyses', label: 'Analysen' },
    { key: 'reports', label: 'Reports' },
    ...(contact.tracking_enabled ? [{ key: 'tracking', label: 'Tracking' }] : []),
    { key: 'data', label: 'Datenquellen' },
    { key: 'documents', label: 'Dokumente' },
  ];

  return (
    <div>
      {/* Header */}
      <div className="flex items-start justify-between mb-5">
        <div>
          <button onClick={() => navigate('/contacts')} className="text-[12px] text-gray-500 hover:text-gray-700 mb-1.5 block">← Zurück zu Kunden</button>
          <h1 className="text-[23px] font-bold mb-0.5">{contact.company || contact.name}</h1>
          <p className="text-gray-500 text-[13.5px]">
            {contact.company ? `${contact.name} · ` : ''}{contact.website || 'Keine Website'}
            {contact.crm_contact_id && <Badge bg="#DBEAFE" color="#3B82F6" className="ml-2">CRM</Badge>}
          </p>
        </div>
        <div className="flex gap-2.5">
          <button
            onClick={() => setShowAnalysis(true)}
            className="px-4 py-2.5 bg-teal text-white rounded-lg text-[13px] font-medium hover:opacity-90"
          >
            + Analyse starten
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-5 bg-bg rounded-lg p-1 w-fit">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 rounded-md text-[13px] transition-colors ${
              tab === t.key ? 'bg-white shadow-sm font-medium' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab: Übersicht */}
      {tab === 'overview' && (
        <div className="grid grid-cols-[1fr_300px] gap-4">
          <div className="space-y-4">
            {/* Kundendaten */}
            <div className="bg-white rounded-xl shadow-sm border border-black/[.07] p-5">
              <h3 className="font-semibold text-[14.5px] mb-3">Kundendaten</h3>
              <div className="grid grid-cols-2 gap-3 text-[13px]">
                <div><span className="text-gray-500">Name:</span> <span className="font-medium ml-1">{contact.name}</span></div>
                <div><span className="text-gray-500">Firma:</span> <span className="font-medium ml-1">{contact.company || '—'}</span></div>
                <div><span className="text-gray-500">E-Mail:</span> <span className="font-medium ml-1">{contact.email || '—'}</span></div>
                <div><span className="text-gray-500">Telefon:</span> <span className="font-medium ml-1">{contact.phone || '—'}</span></div>
                <div className="col-span-2"><span className="text-gray-500">Website:</span> <span className="font-medium ml-1">{contact.website || '—'}</span></div>
              </div>
            </div>

            {/* Letzte Analysen */}
            <div className="bg-white rounded-xl shadow-sm border border-black/[.07] p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-[14.5px]">Letzte Analysen</h3>
                <button onClick={() => setTab('analyses')} className="text-[12px] text-teal font-medium">Alle →</button>
              </div>
              {(contact.analyses || []).length === 0 ? (
                <p className="text-gray-400 text-sm py-4 text-center">Noch keine Analysen</p>
              ) : (
                contact.analyses.slice(0, 5).map((a) => (
                  <div key={a.id} className="flex items-center gap-3 py-2.5 border-b border-gray-100 last:border-0">
                    <ScorePill score={a.score || 0} />
                    <div className="flex-1 min-w-0">
                      <div className="text-[12.5px] font-medium truncate">{a.url}</div>
                      <div className="text-[11px] text-gray-500">{a.page_type} · {new Date(a.created_at).toLocaleDateString('de')}</div>
                    </div>
                    <Badge bg="#E6F4F2" color="#0C9488">{a.status}</Badge>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Rechte Spalte */}
          <div className="space-y-4">
            {/* Tracking */}
            <div className="bg-white rounded-xl shadow-sm border border-black/[.07] p-5">
              <h3 className="font-semibold text-[14.5px] mb-3">Tracking</h3>
              <div className="flex items-center justify-between mb-3">
                <span className="text-[13px]">Tracking aktiv</span>
                <button
                  onClick={toggleTracking}
                  className={`w-10 h-[22px] rounded-full transition-colors relative ${contact.tracking_enabled ? 'bg-teal' : 'bg-gray-300'}`}
                >
                  <div className={`w-[18px] h-[18px] bg-white rounded-full absolute top-[2px] transition-all ${contact.tracking_enabled ? 'left-[20px]' : 'left-[2px]'}`} />
                </button>
              </div>
              {contact.tracking_enabled && contact.tracking_code && (
                <div className="bg-bg rounded-lg p-3 mt-2">
                  <div className="text-[11px] text-gray-500 mb-1">Tracking-Code</div>
                  <code className="text-[12px] font-mono text-teal">{contact.tracking_code}</code>
                </div>
              )}
            </div>

            {/* Quick Stats */}
            <div className="bg-white rounded-xl shadow-sm border border-black/[.07] p-5">
              <h3 className="font-semibold text-[14.5px] mb-3">Statistik</h3>
              <div className="space-y-2 text-[13px]">
                <div className="flex justify-between"><span className="text-gray-500">Analysen</span><span className="font-medium">{(contact.analyses || []).length}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Dokumente</span><span className="font-medium">{(contact.documents || []).length}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Datenquellen</span><span className="font-medium">{(contact.data_sources || []).length}</span></div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tab: Tracking Dashboard */}
      {tab === 'tracking' && contact.tracking_enabled && (() => {
        const td = trackingData.data;
        const s = td?.summary || {};
        const formatDuration = (ms) => {
          if (!ms) return '0s';
          const sec = Math.round(ms / 1000);
          if (sec < 60) return sec + 's';
          return Math.floor(sec / 60) + 'm ' + (sec % 60) + 's';
        };
        const maxViews = Math.max(...(td?.pageviews_by_day || []).map(d => d.count), 1);

        return (
          <div>
            {/* Header mit Zeitraum-Filter und Aktionen */}
            <div className="flex justify-between items-center mb-4">
              <div className="flex gap-1 bg-bg rounded-lg p-1">
                {[7, 30, 90].map((d) => (
                  <button
                    key={d}
                    onClick={() => setTrackingDays(d)}
                    className={`px-3 py-1.5 rounded-md text-[12px] transition-colors ${
                      trackingDays === d ? 'bg-white shadow-sm font-medium' : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {d} Tage
                  </button>
                ))}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowSnippet(true)}
                  className="px-3 py-2 border border-gray-200 rounded-lg text-[12px] hover:bg-gray-50"
                >
                  Snippet anzeigen
                </button>
                <button
                  onClick={() => setShowPurgeConfirm(true)}
                  className="px-3 py-2 border border-red-200 text-red-600 rounded-lg text-[12px] hover:bg-red-50"
                >
                  Daten löschen (DSGVO)
                </button>
              </div>
            </div>

            {trackingData.isLoading ? (
              <div className="text-center py-12 text-gray-400 text-sm">Tracking-Daten werden geladen…</div>
            ) : !td || s.pageviews === 0 ? (
              <div className="bg-white rounded-xl shadow-sm border border-black/[.07] p-8 text-center">
                <div className="text-3xl mb-3">📡</div>
                <h3 className="font-semibold text-[15px] mb-1">Noch keine Tracking-Daten</h3>
                <p className="text-[13px] text-gray-500 mb-4">Binde das Tracking-Snippet auf der Website von {contact.name} ein.</p>
                <button onClick={() => setShowSnippet(true)} className="px-4 py-2 bg-teal text-white rounded-lg text-[13px] font-medium hover:opacity-90">
                  Snippet anzeigen
                </button>
              </div>
            ) : (
              <>
                {/* KPI Cards */}
                <div className="grid grid-cols-5 gap-3 mb-4">
                  {[
                    { label: 'Seitenaufrufe', value: s.pageviews?.toLocaleString('de') },
                    { label: 'Sessions', value: s.unique_sessions?.toLocaleString('de') },
                    { label: 'Conversions', value: s.conversions?.toLocaleString('de') },
                    { label: 'Conv. Rate', value: s.conversion_rate + '%' },
                    { label: 'Ø Verweildauer', value: formatDuration(s.avg_duration_ms) },
                  ].map((kpi) => (
                    <div key={kpi.label} className="bg-white rounded-xl shadow-sm border border-black/[.07] p-4">
                      <div className="text-[11px] text-gray-500 mb-1">{kpi.label}</div>
                      <div className="text-[20px] font-bold">{kpi.value}</div>
                    </div>
                  ))}
                </div>

                {/* Pageviews Chart (simple bar) */}
                <div className="bg-white rounded-xl shadow-sm border border-black/[.07] p-5 mb-4">
                  <h3 className="font-semibold text-[14px] mb-3">Seitenaufrufe ({trackingDays} Tage)</h3>
                  <div className="flex items-end gap-[2px] h-[120px]">
                    {(td.pageviews_by_day || []).map((d) => (
                      <div key={d.date} className="flex-1 flex flex-col items-center justify-end h-full group relative">
                        <div
                          className="w-full bg-teal/80 rounded-t-sm min-h-[2px] transition-all hover:bg-teal"
                          style={{ height: (d.count / maxViews) * 100 + '%' }}
                        />
                        <div className="absolute -top-6 bg-navy text-white text-[10px] px-1.5 py-0.5 rounded opacity-0 group-hover:opacity-100 whitespace-nowrap pointer-events-none">
                          {new Date(d.date).toLocaleDateString('de', { day: '2-digit', month: '2-digit' })}: {d.count}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-4">
                  {/* Geräte */}
                  <div className="bg-white rounded-xl shadow-sm border border-black/[.07] p-5">
                    <h3 className="font-semibold text-[14px] mb-3">Geräte</h3>
                    <div className="space-y-2.5">
                      {(td.devices || []).map((d) => {
                        const total = (td.devices || []).reduce((a, b) => a + b.count, 0);
                        const pct = total > 0 ? Math.round((d.count / total) * 100) : 0;
                        const icon = d.device_type === 'desktop' ? '🖥' : d.device_type === 'mobile' ? '📱' : '📲';
                        return (
                          <div key={d.device_type}>
                            <div className="flex justify-between text-[12.5px] mb-1">
                              <span>{icon} {d.device_type || 'Unbekannt'}</span>
                              <span className="text-gray-500">{pct}% ({d.count})</span>
                            </div>
                            <Bar pct={pct} color="#0C9488" />
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Bildschirmauflösungen */}
                  <div className="bg-white rounded-xl shadow-sm border border-black/[.07] p-5">
                    <h3 className="font-semibold text-[14px] mb-3">Bildschirmauflösungen</h3>
                    <div className="space-y-2.5">
                      {(td.resolutions || []).slice(0, 6).map((r) => {
                        const total = (td.resolutions || []).reduce((a, b) => a + b.count, 0);
                        const pct = total > 0 ? Math.round((r.count / total) * 100) : 0;
                        return (
                          <div key={r.screen_resolution}>
                            <div className="flex justify-between text-[12.5px] mb-1">
                              <span className="font-mono text-[11.5px]">{r.screen_resolution || '—'}</span>
                              <span className="text-gray-500">{pct}%</span>
                            </div>
                            <Bar pct={pct} color="#0C9488" />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-4">
                  {/* Top Seiten */}
                  <div className="bg-white rounded-xl shadow-sm border border-black/[.07] p-5">
                    <h3 className="font-semibold text-[14px] mb-3">Top Seiten</h3>
                    <div className="space-y-2">
                      {(td.top_pages || []).map((p, i) => (
                        <div key={i} className="flex items-center gap-2 py-1.5 border-b border-gray-100 last:border-0">
                          <span className="text-[11px] text-gray-400 w-5">{i + 1}.</span>
                          <div className="flex-1 min-w-0">
                            <div className="text-[12px] font-medium truncate">{p.page_title || p.page_url}</div>
                            <div className="text-[10.5px] text-gray-400 truncate">{p.page_url}</div>
                          </div>
                          <span className="text-[12px] text-gray-500">{p.views}x</span>
                          <span className="text-[11px] text-gray-400">{formatDuration(p.avg_duration)}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Conversions & Referrer */}
                  <div className="space-y-4">
                    {(td.conversion_labels || []).length > 0 && (
                      <div className="bg-white rounded-xl shadow-sm border border-black/[.07] p-5">
                        <h3 className="font-semibold text-[14px] mb-3">Conversions</h3>
                        <div className="space-y-2">
                          {td.conversion_labels.map((c) => (
                            <div key={c.conversion_label} className="flex justify-between text-[12.5px] py-1 border-b border-gray-100 last:border-0">
                              <span>{c.conversion_label || 'Ohne Label'}</span>
                              <span className="font-medium">{c.count}x</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="bg-white rounded-xl shadow-sm border border-black/[.07] p-5">
                      <h3 className="font-semibold text-[14px] mb-3">Referrer</h3>
                      <div className="space-y-2">
                        {(td.referrers || []).length === 0 ? (
                          <p className="text-gray-400 text-[12.5px]">Keine Referrer-Daten</p>
                        ) : td.referrers.map((r, i) => (
                          <div key={i} className="flex justify-between text-[12.5px] py-1 border-b border-gray-100 last:border-0">
                            <span className="truncate max-w-[200px]">{r.referrer}</span>
                            <span className="text-gray-500">{r.count}x</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        );
      })()}

      {/* Tab: Datenquellen */}
      {tab === 'data' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-semibold text-[14.5px]">Datenquellen</h3>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {/* Tracking-Snippet Karte */}
            <div className="bg-white rounded-xl shadow-sm border border-black/[.07] p-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-lg">📡</span>
                <span className="text-[13px] font-medium">Tracking-Snippet</span>
              </div>
              {contact.tracking_enabled ? (
                <div className="flex items-center gap-2">
                  <Badge bg="#D1FAE5" color="#10B981">Aktiv</Badge>
                  <button onClick={() => setTab('tracking')} className="text-[12px] text-teal font-medium hover:underline">
                    Tracking-Dashboard →
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Badge bg="#F3F4F6" color="#6B7280">Inaktiv</Badge>
                  <button onClick={toggleTracking} className="text-[12px] text-teal font-medium hover:underline">
                    Aktivieren
                  </button>
                </div>
              )}
            </div>

            {/* Google Analytics Karte */}
            <div className="bg-white rounded-xl shadow-sm border border-black/[.07] p-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-lg">📊</span>
                <span className="text-[13px] font-medium">Google Analytics</span>
              </div>
              {(contact.data_sources || []).find(ds => ds.type === 'google_analytics' && ds.status === 'connected') ? (
                <div className="flex items-center gap-2">
                  <Badge bg="#D1FAE5" color="#10B981">Verbunden</Badge>
                  <span className="text-[11px] text-gray-400">
                    {(contact.data_sources || []).find(ds => ds.type === 'google_analytics')?.label || 'GA4'}
                  </span>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Badge bg="#DBEAFE" color="#3B82F6">Nicht verbunden</Badge>
                  <button
                    onClick={() => {
                      // TODO: GA4 OAuth Flow
                      alert('Google Analytics Anbindung wird in Kürze verfügbar sein. Bitte trage den Kunden als Admin in dein GA4-Konto ein.');
                    }}
                    className="text-[12px] text-blue-600 font-medium hover:underline"
                  >
                    Anbinden →
                  </button>
                </div>
              )}
            </div>

            {/* Search Console Karte */}
            <div className="bg-white rounded-xl shadow-sm border border-black/[.07] p-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-lg">🔍</span>
                <span className="text-[13px] font-medium">Search Console</span>
              </div>
              {(contact.data_sources || []).find(ds => ds.type === 'search_console' && ds.status === 'connected') ? (
                <Badge bg="#D1FAE5" color="#10B981">Verbunden</Badge>
              ) : (
                <div className="flex items-center gap-2">
                  <Badge bg="#F3F4F6" color="#6B7280">Nicht verbunden</Badge>
                  <span className="text-[11px] text-gray-400">Kommt bald</span>
                </div>
              )}
            </div>

            {/* Manuelle Daten Karte */}
            <div className="bg-white rounded-xl shadow-sm border border-black/[.07] p-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-lg">📄</span>
                <span className="text-[13px] font-medium">Manuelle Daten</span>
              </div>
              <div className="flex items-center gap-2">
                <Badge bg={(contact.documents || []).length > 0 ? '#D1FAE5' : '#F3F4F6'} color={(contact.documents || []).length > 0 ? '#10B981' : '#6B7280'}>
                  {(contact.documents || []).length > 0 ? `${(contact.documents || []).length} Dokumente` : 'Keine Daten'}
                </Badge>
                <button onClick={() => setTab('documents')} className="text-[12px] text-teal font-medium hover:underline">
                  {(contact.documents || []).length > 0 ? 'Dokumente →' : 'Hochladen →'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tab: Dokumente */}
      {tab === 'documents' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-semibold text-[14.5px]">Kunden-Dokumente (Vector Store)</h3>
            <button
              onClick={() => fileRef.current?.click()}
              className="px-4 py-2 bg-teal text-white rounded-lg text-[13px] font-medium hover:opacity-90"
            >
              + Dokument hochladen
            </button>
            <input ref={fileRef} type="file" className="hidden" onChange={handleUpload} accept=".pdf,.docx,.xlsx,.csv,.txt,.md,.png,.jpg,.jpeg" />
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-black/[.07]">
            {(contact.documents || []).map((doc) => (
              <div key={doc.id} className="flex items-center gap-3 px-5 py-3.5 border-b border-gray-100 last:border-0">
                <div className="w-9 h-9 rounded-lg bg-teal-light text-teal flex items-center justify-center text-sm">
                  {doc.mime_type?.includes('pdf') ? '📕' : doc.mime_type?.includes('csv') ? '📊' : doc.mime_type?.includes('image') ? '🖼' : '📄'}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-medium truncate">{doc.name}</div>
                  <div className="text-[11px] text-gray-500">
                    {(doc.file_size / 1024).toFixed(0)} KB · {new Date(doc.created_at).toLocaleDateString('de')}
                  </div>
                </div>
                <Badge
                  bg={doc.status === 'processed' ? '#D1FAE5' : doc.status === 'uploading' ? '#FEF3C7' : '#FEE2E2'}
                  color={doc.status === 'processed' ? '#10B981' : doc.status === 'uploading' ? '#F59E0B' : '#EF4444'}
                >
                  {doc.status === 'processed' ? 'Verarbeitet' : doc.status === 'uploading' ? 'Upload…' : doc.status}
                </Badge>
              </div>
            ))}
            {(contact.documents || []).length === 0 && (
              <div className="py-12 text-center text-gray-400 text-sm">
                Keine Dokumente. Lade GA-Exports, Screenshots oder CSV-Daten hoch.
              </div>
            )}
          </div>
          <p className="text-[11.5px] text-gray-400 mt-3">
            Dokumente werden in den Kunden-Vector-Store geladen und bei Analysen als Kontext verwendet.
          </p>
        </div>
      )}

      {/* Tab: Analysen */}
      {tab === 'analyses' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-semibold text-[14.5px]">Alle Analysen</h3>
            <button onClick={() => setShowAnalysis(true)} className="px-4 py-2 bg-teal text-white rounded-lg text-[13px] font-medium hover:opacity-90">
              + Neue Analyse
            </button>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-black/[.07]">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 text-left">
                  <th className="px-5 py-3.5 text-[12px] text-gray-500 font-medium">URL</th>
                  <th className="px-5 py-3.5 text-[12px] text-gray-500 font-medium">Typ</th>
                  <th className="px-5 py-3.5 text-[12px] text-gray-500 font-medium">Datum</th>
                  <th className="px-5 py-3.5 text-[12px] text-gray-500 font-medium">Score</th>
                  <th className="px-5 py-3.5 text-[12px] text-gray-500 font-medium">Provider</th>
                  <th className="px-5 py-3.5 text-[12px] text-gray-500 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {(contact.analyses || []).map((a) => (
                  <tr key={a.id} onClick={() => a.status === 'done' && navigate(`/cro/analyses/${a.id}`)} className={`border-b border-gray-100 last:border-0 hover:bg-gray-50/50 ${a.status === 'done' ? 'cursor-pointer' : ''}`}>
                    <td className="px-5 py-3.5 text-[13px] font-medium truncate max-w-[260px]">{a.url}</td>
                    <td className="px-5 py-3.5"><Badge bg="#E6F4F2" color="#0C9488">{a.page_type}</Badge></td>
                    <td className="px-5 py-3.5 text-[12.5px] text-gray-500">{new Date(a.created_at).toLocaleDateString('de')}</td>
                    <td className="px-5 py-3.5">{a.score ? <ScorePill score={a.score} /> : '—'}</td>
                    <td className="px-5 py-3.5"><Badge bg="#F3F4F6" color="#6B7280">{a.provider || '—'}</Badge></td>
                    <td className="px-5 py-3.5">
                      <Badge
                        bg={a.status === 'done' ? '#D1FAE5' : a.status === 'processing' ? '#FEF3C7' : a.status === 'failed' ? '#FEE2E2' : '#F3F4F6'}
                        color={a.status === 'done' ? '#10B981' : a.status === 'processing' ? '#F59E0B' : a.status === 'failed' ? '#EF4444' : '#6B7280'}
                      >
                        {a.status}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {(contact.analyses || []).length === 0 && (
              <div className="py-12 text-center text-gray-400 text-sm">Noch keine Analysen für diesen Kunden.</div>
            )}
          </div>
        </div>
      )}

      {/* Tab: Reports */}
      {tab === 'reports' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-semibold text-[14.5px]">Reports</h3>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-black/[.07]">
            {customerReports.length > 0 ? (
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 text-left">
                    <th className="px-5 py-3.5 text-[12px] text-gray-500 font-medium">URL</th>
                    <th className="px-5 py-3.5 text-[12px] text-gray-500 font-medium">Datum</th>
                    <th className="px-5 py-3.5 text-[12px] text-gray-500 font-medium">Score</th>
                    <th className="px-5 py-3.5 text-[12px] text-gray-500 font-medium">Sharing</th>
                    <th className="px-5 py-3.5 text-[12px] text-gray-500 font-medium text-right">Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  {customerReports.map((r) => (
                    <tr key={r.id} className="border-b border-gray-100 last:border-0 hover:bg-gray-50/50">
                      <td className="px-5 py-3.5 text-[13px] font-medium truncate max-w-[250px]">{r.analysis?.url || '—'}</td>
                      <td className="px-5 py-3.5 text-[12.5px] text-gray-500">{new Date(r.created_at).toLocaleDateString('de')}</td>
                      <td className="px-5 py-3.5">{r.analysis?.score ? <ScorePill score={r.analysis.score} /> : '—'}</td>
                      <td className="px-5 py-3.5">
                        {r.is_shared ? <Badge bg="#D1FAE5" color="#10B981">Geteilt</Badge> : <Badge bg="#F3F4F6" color="#6B7280">Privat</Badge>}
                      </td>
                      <td className="px-5 py-3.5 text-right">
                        <div className="flex gap-2 justify-end">
                          <button onClick={() => navigate(`/cro/analyses/${r.analysis?.id}`)} className="text-[12px] text-teal font-medium hover:underline">Ansehen</button>
                          <button onClick={() => window.open(`/api/pdf/${r.id}`, '_blank')} className="text-[12px] text-blue-600 font-medium hover:underline">PDF</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="py-12 text-center text-gray-400 text-sm">
                Noch keine Reports. Erstelle einen Report aus einer abgeschlossenen Analyse.
              </div>
            )}
          </div>
        </div>
      )}

      {/* Analyse starten Modal */}
      {showAnalysis && (
        <Modal onClose={() => setShowAnalysis(false)}>
          <div className="w-[420px]">
            <h2 className="text-[17px] font-bold mb-1">Analyse starten</h2>
            <p className="text-[13px] text-gray-500 mb-5">Für {contact.name}{contact.company ? ` (${contact.company})` : ''}</p>

            <div className="mb-4">
              <label className="text-[12px] text-gray-500 block mb-1.5 font-medium">URL</label>
              <input
                className="w-full border border-gray-200 rounded-lg px-3.5 py-2.5 text-[13px] outline-none focus:border-teal"
                placeholder={contact.website || 'https://kunde.de/seite'}
                value={analysisForm.url}
                onChange={(e) => setAnalysisForm({ ...analysisForm, url: e.target.value })}
              />
            </div>

            <div className="mb-5">
              <label className="text-[12px] text-gray-500 block mb-2 font-medium">Seitentyp / Tag</label>
              <div className="flex flex-wrap gap-2">
                {allTags.map((t) => (
                  <button
                    key={t.slug}
                    onClick={() => setAnalysisForm({ ...analysisForm, page_type: t.slug })}
                    className={`rounded-lg py-2 px-3 text-[11px] border transition-all ${
                      analysisForm.page_type === t.slug
                        ? 'text-white font-semibold'
                        : 'bg-bg border-gray-200 text-gray-500 hover:border-gray-300'
                    }`}
                    style={analysisForm.page_type === t.slug ? { backgroundColor: t.color, borderColor: t.color } : {}}
                  >
                    {t.name} ({t.factors_count})
                  </button>
                ))}
              </div>
            </div>

            <div className="mb-5">
              <label className="text-[12px] text-gray-500 block mb-2 font-medium">LLM Provider</label>
              <div className="flex gap-2">
                {[
                  { key: '', label: 'Standard' },
                  ...providers.map(p => ({ key: p.name, label: p.name === 'openai' ? 'OpenAI' : p.name === 'claude' ? 'Claude' : 'Self-Hosted', available: p.available })),
                ].map((p) => (
                  <button
                    key={p.key}
                    onClick={() => setAnalysisForm({ ...analysisForm, provider: p.key })}
                    disabled={p.available === false}
                    className={`flex-1 rounded-lg py-2 px-2 text-[12px] border transition-all ${
                      analysisForm.provider === p.key
                        ? 'bg-sidebar border-sidebar text-white font-semibold'
                        : p.available === false
                          ? 'bg-gray-100 border-gray-200 text-gray-300 cursor-not-allowed'
                          : 'bg-bg border-gray-200 text-gray-500 hover:border-gray-300'
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex gap-2.5 justify-end">
              <button onClick={() => setShowAnalysis(false)} className="border border-gray-200 rounded-lg px-4 py-2 text-[13px] hover:bg-gray-50">
                Abbrechen
              </button>
              <button
                onClick={handleStartAnalysis}
                disabled={!analysisForm.url || !analysisForm.page_type || startAnalysis.isPending}
                className="bg-teal text-white rounded-lg px-4 py-2 text-[13px] font-medium hover:opacity-90 disabled:opacity-50"
              >
                {startAnalysis.isPending ? 'Wird gestartet…' : 'Analyse starten'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Tracking Snippet Modal */}
      {showSnippet && (
        <Modal onClose={() => setShowSnippet(false)}>
          <div className="w-[560px]">
            <h2 className="text-[17px] font-bold mb-1">Tracking-Snippet</h2>
            <p className="text-[13px] text-gray-500 mb-4">
              Dieses Snippet auf der Website von {contact.name} einbinden.
            </p>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-[12.5px] text-amber-800">
              <strong>DSGVO-Hinweis:</strong> Das Snippet darf erst nach Einwilligung des Website-Besuchers geladen werden (Cookie-Banner / Consent Management).
            </div>
            {snippetData.isLoading ? (
              <div className="text-center py-6 text-gray-400 text-sm">Wird generiert…</div>
            ) : (
              <pre className="bg-gray-900 text-green-400 rounded-lg p-4 text-[11px] overflow-x-auto max-h-[300px] overflow-y-auto font-mono">
                {snippetData.data?.snippet}
              </pre>
            )}
            <div className="flex justify-between items-center mt-4">
              <span className="text-[11px] text-gray-400">Code: {contact.tracking_code}</span>
              <button
                onClick={() => { navigator.clipboard.writeText(snippetData.data?.snippet || ''); }}
                className="px-4 py-2 bg-teal text-white rounded-lg text-[13px] font-medium hover:opacity-90"
              >
                Kopieren
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* DSGVO Daten löschen Modal */}
      {showPurgeConfirm && (
        <Modal onClose={() => setShowPurgeConfirm(false)}>
          <div className="w-[400px]">
            <h2 className="text-[17px] font-bold mb-1 text-red-600">Tracking-Daten löschen</h2>
            <p className="text-[13px] text-gray-500 mb-4">
              Alle gesammelten Tracking-Daten für <strong>{contact.name}</strong> werden unwiderruflich gelöscht. Diese Aktion kann nicht rückgängig gemacht werden.
            </p>
            <div className="flex gap-2.5 justify-end">
              <button onClick={() => setShowPurgeConfirm(false)} className="border border-gray-200 rounded-lg px-4 py-2 text-[13px] hover:bg-gray-50">
                Abbrechen
              </button>
              <button
                onClick={async () => {
                  await purgeTracking.mutateAsync(contact.id);
                  setShowPurgeConfirm(false);
                }}
                className="bg-red-600 text-white rounded-lg px-4 py-2 text-[13px] font-medium hover:bg-red-700"
              >
                {purgeTracking.isPending ? 'Wird gelöscht…' : 'Endgültig löschen'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Datenquelle hinzufügen Modal */}
      {showDataSource && (
        <Modal onClose={() => setShowDataSource(false)}>
          <div className="w-[420px]">
            <h2 className="text-[17px] font-bold mb-1">Datenquelle hinzufügen</h2>
            <p className="text-[13px] text-gray-500 mb-5">Verknüpfe eine Datenquelle mit {contact.name}</p>

            <div className="grid grid-cols-2 gap-2.5 mb-4">
              {DATA_SOURCE_TYPES.map((t) => (
                <button
                  key={t.value}
                  onClick={() => setDsForm({ ...dsForm, type: t.value, label: t.label })}
                  className={`p-3 rounded-lg border text-left transition-all ${
                    dsForm.type === t.value ? 'border-teal bg-teal-light' : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <span className="text-lg block mb-1">{t.icon}</span>
                  <span className="text-[12.5px] font-medium">{t.label}</span>
                </button>
              ))}
            </div>

            <div className="mb-5">
              <label className="text-[12px] text-gray-500 block mb-1.5 font-medium">Bezeichnung</label>
              <input
                className="w-full border border-gray-200 rounded-lg px-3.5 py-2.5 text-[13px] outline-none focus:border-teal"
                placeholder="z.B. GA4 Property 12345"
                value={dsForm.label}
                onChange={(e) => setDsForm({ ...dsForm, label: e.target.value })}
              />
            </div>

            <div className="flex gap-2.5 justify-end">
              <button onClick={() => setShowDataSource(false)} className="border border-gray-200 rounded-lg px-4 py-2 text-[13px] hover:bg-gray-50">
                Abbrechen
              </button>
              <button
                onClick={handleAddDataSource}
                disabled={!dsForm.type}
                className="bg-teal text-white rounded-lg px-4 py-2 text-[13px] font-medium hover:opacity-90 disabled:opacity-50"
              >
                Hinzufügen
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
